import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "@/config";
import { useAuth } from "@/hooks/useAuth";
import { Calendar, Car, User, Wrench, CheckCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const SERVICE_TYPES = [
    "Oil Change", "Tire Rotation", "Brake Service", "Engine Diagnostic",
    "Transmission Service", "AC / Heating", "Battery Replacement",
    "Wheel Alignment", "Inspection / Emissions", "Other",
];

export default function BookAppointmentPage() {
    const { shopId } = useParams();
    const navigate   = useNavigate();
    const { user }   = useAuth();

    const [shopName, setShopName] = useState("");
    const [form, setForm] = useState({
        customer_name:  "",
        customer_phone: "",
        customer_email: "",
        vehicle_year:   "",
        vehicle_make:   "",
        vehicle_model:  "",
        vehicle_vin:    "",
        service_type:   "",
        notes:          "",
        appt_date:      "",
        appt_time:      "",
    });
    const [loading,   setLoading]   = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error,     setError]     = useState<string | null>(null);

    useEffect(() => {
        axios.get(`${API_URL}/repair-shops/${shopId}`, { withCredentials: true })
            .then((r) => setShopName(r.data.shop?.shop_name ?? ""));
    }, [shopId]);

    const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
        setForm((f) => ({ ...f, [k]: e.target.value }));

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!form.service_type || !form.appt_date || !form.appt_time) {
            setError("Please select a service type, date, and time.");
            return;
        }
        setLoading(true);
        setError(null);
        try {
            await axios.post(
                `${API_URL}/repair-shops/${shopId}/appointments`,
                { ...form, customer_id: user?.id ?? undefined },
                { withCredentials: true }
            );
            setSubmitted(true);
        } catch (err: any) {
            setError(err?.response?.data?.message ?? "Booking failed. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    if (submitted) {
        return (
            <div className="max-w-lg mx-auto py-16 px-4 text-center space-y-4">
                <CheckCircle className="h-14 w-14 text-green-500 mx-auto" />
                <h2 className="text-2xl font-bold">Appointment Booked!</h2>
                <p className="text-muted-foreground">
                    Your service appointment at <strong>{shopName}</strong> has been scheduled for{" "}
                    <strong>{form.appt_date}</strong> at <strong>{form.appt_time}</strong>.
                </p>
                {user && (
                    <Button onClick={() => navigate("/customer/repair-orders")} className="mt-2">
                        View My Repair Orders
                    </Button>
                )}
                <Button variant="outline" onClick={() => navigate("/repair-shops")}>
                    Find Another Shop
                </Button>
            </div>
        );
    }

    return (
        <div className="max-w-2xl mx-auto py-8 px-4 space-y-6">
            <div>
                <h1 className="text-2xl font-bold">Book an Appointment</h1>
                {shopName && <p className="text-muted-foreground mt-1">{shopName}</p>}
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Contact Info */}
                <div className="bg-card rounded-xl border p-5 space-y-4">
                    <h2 className="font-semibold flex items-center gap-2"><User className="h-4 w-4" /> Your Info</h2>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs uppercase font-medium mb-1 block">Full Name</label>
                            <Input value={form.customer_name} onChange={set("customer_name")} placeholder="Jane Smith" required />
                        </div>
                        <div>
                            <label className="text-xs uppercase font-medium mb-1 block">Phone</label>
                            <Input value={form.customer_phone} onChange={set("customer_phone")} placeholder="(555) 000-0000" />
                        </div>
                        <div className="sm:col-span-2">
                            <label className="text-xs uppercase font-medium mb-1 block">Email</label>
                            <Input type="email" value={form.customer_email} onChange={set("customer_email")} placeholder="you@example.com" />
                        </div>
                    </div>
                </div>

                {/* Vehicle */}
                <div className="bg-card rounded-xl border p-5 space-y-4">
                    <h2 className="font-semibold flex items-center gap-2"><Car className="h-4 w-4" /> Vehicle</h2>
                    <div className="grid sm:grid-cols-3 gap-3">
                        <div>
                            <label className="text-xs uppercase font-medium mb-1 block">Year</label>
                            <Input value={form.vehicle_year} onChange={set("vehicle_year")} placeholder="2021" />
                        </div>
                        <div>
                            <label className="text-xs uppercase font-medium mb-1 block">Make</label>
                            <Input value={form.vehicle_make} onChange={set("vehicle_make")} placeholder="Toyota" />
                        </div>
                        <div>
                            <label className="text-xs uppercase font-medium mb-1 block">Model</label>
                            <Input value={form.vehicle_model} onChange={set("vehicle_model")} placeholder="Camry" />
                        </div>
                        <div className="sm:col-span-3">
                            <label className="text-xs uppercase font-medium mb-1 block">VIN (optional)</label>
                            <Input value={form.vehicle_vin} onChange={set("vehicle_vin")} placeholder="1HGBH41JXMN109186" />
                        </div>
                    </div>
                </div>

                {/* Service */}
                <div className="bg-card rounded-xl border p-5 space-y-4">
                    <h2 className="font-semibold flex items-center gap-2"><Wrench className="h-4 w-4" /> Service Details</h2>
                    <div>
                        <label className="text-xs uppercase font-medium mb-1 block">Service Type *</label>
                        <select
                            className="w-full border rounded-lg px-3 py-2 text-sm bg-background"
                            value={form.service_type}
                            onChange={set("service_type")}
                            required
                        >
                            <option value="">— Select a service —</option>
                            {SERVICE_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="text-xs uppercase font-medium mb-1 block">Additional Notes</label>
                        <textarea
                            className="w-full border rounded-lg px-3 py-2 text-sm bg-background resize-none"
                            rows={3}
                            value={form.notes}
                            onChange={set("notes")}
                            placeholder="Any additional information about the issue…"
                        />
                    </div>
                </div>

                {/* Date & Time */}
                <div className="bg-card rounded-xl border p-5 space-y-4">
                    <h2 className="font-semibold flex items-center gap-2"><Calendar className="h-4 w-4" /> Schedule</h2>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs uppercase font-medium mb-1 block">Preferred Date *</label>
                            <Input type="date" value={form.appt_date} onChange={set("appt_date")} required />
                        </div>
                        <div>
                            <label className="text-xs uppercase font-medium mb-1 block">Preferred Time *</label>
                            <Input type="time" value={form.appt_time} onChange={set("appt_time")} required />
                        </div>
                    </div>
                </div>

                {error && <p className="text-red-500 text-sm text-center">{error}</p>}

                <Button
                    type="submit"
                    className="w-full bg-indigo-600 text-white hover:bg-indigo-700"
                    disabled={loading}
                >
                    {loading ? "Booking…" : "Confirm Appointment"}
                </Button>
            </form>
        </div>
    );
}
